/**
 * Homepage — 12 sections per BR-005 blueprint, BR-006 story arc, BR-010 visual system.
 */
import { Link } from "react-router-dom";
import useAttributionCapture from "@/hooks/useAttributionCapture";
import {
  Sparkles, ArrowRight, Play, Building2, Brain, TrendingUp, LayoutDashboard,
  Shield, Zap, BadgeCheck, LineChart, Users2, Target, Check,
  ArrowUpRight, Layers, Rocket, ChevronRight,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "./MarketingChrome";

/* Reusable atoms */
const Section = ({ children, className = "", bg = "white", tid }) => {
  const bgClass = bg === "navy"
    ? "bg-[#0B1F3A] text-white"
    : bg === "slate"
    ? "bg-slate-50 text-slate-900"
    : "bg-white text-slate-900";
  return (
    <section data-testid={tid} className={`${bgClass} ${className}`}>
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">{children}</div>
    </section>
  );
};
const Kicker = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">{children}</div>
);
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold text-white">
    {children}
  </span>
);
const ProductScreen = ({ title, children }) => (
  <div className="mt-8 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
    <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span>
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
      <span className="ml-3 text-xs text-slate-500 font-mono">{title}</span>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

export default function Homepage() {
  useAttributionCapture();
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <MarketingHeader />

      {/* 1 · HERO */}
      <section data-testid="section-hero" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.25),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-3xl">
            <Pill>
              <Sparkles className="w-3 h-3 text-emerald-400" />
              New · BP-025 AI Acquisition Coach shipped
            </Pill>
            <h1 data-testid="hero-headline" className="mt-6 text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              The AI Operating System<br />
              <span className="text-emerald-400">for Real Estate Investors.</span>
            </h1>
            <p data-testid="hero-subcopy" className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl">
              Smarter decisions. Faster closings. One platform. Every recommendation is backed by evidence — so your team moves with confidence, not guesswork.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/auth?mode=signup"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#demo"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
              >
                <Play className="w-4 h-4" /> Watch 3-Minute Demo
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="flex items-center gap-1"><BadgeCheck className="w-4 h-4 text-emerald-400" /> Evidence-backed AI</span>
              <span className="flex items-center gap-1"><BadgeCheck className="w-4 h-4 text-emerald-400" /> Every strategy, one platform</span>
              <span className="flex items-center gap-1"><BadgeCheck className="w-4 h-4 text-emerald-400" /> Enterprise-grade governance</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · PROBLEM */}
      <Section tid="section-problem" bg="slate">
        <Kicker>The Problem</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Investors don't struggle because they lack effort. They struggle because <span className="text-slate-500">critical information is scattered across ten disconnected tools.</span>
        </h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-6 gap-3">
          {["CRM", "Spreadsheets", "Calculators", "Email", "Marketing", "AI tools", "Comps", "Contracts", "Reporting", "Skip-tracing", "SMS", "Buyers list"].map((t) => (
            <div key={t} className="rounded-lg border border-slate-300 bg-white px-3 py-4 text-center text-sm text-slate-600 line-through">
              {t}
            </div>
          ))}
        </div>
        <p className="mt-8 text-lg text-slate-600 max-w-2xl">
          Slower decisions. Inconsistent execution. Missed opportunities. And no single source of truth about your business.
        </p>
      </Section>

      {/* 3 · SOLUTION */}
      <Section tid="section-solution" bg="white">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>The Solution</Kicker>
            <h2 className="mt-2 text-3xl md:text-5xl font-bold leading-tight">
              One intelligent platform.<br />
              <span className="text-emerald-600">Every stage of the lifecycle.</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              iSpeedToClose unifies leads, analysis, strategy, negotiation, disposition, and executive intelligence into a single command surface. Explainable AI guides every next-best action.
            </p>
            <div className="mt-6 space-y-2 text-sm text-slate-700">
              {[
                "Replace workflows, not just software",
                "Every recommendation cites its source engine",
                "Confidence capped when data is incomplete — never invented",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#1A2B44] p-8 text-white">
            <div className="text-xs uppercase tracking-widest text-emerald-400">Deterministic + Explainable</div>
            <div className="mt-1 text-2xl font-bold">Lead → Analysis → Strategy → Deal → Insight</div>
            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {[
                ["BP-005", "Deal Score"], ["BP-014", "Strategy Compare"],
                ["BP-022", "Buyer Intel"], ["BP-023", "Portfolio"],
                ["BP-024", "Market Intel"], ["BP-025", "AI Coach"],
              ].map(([bp, name]) => (
                <div key={bp} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                  <div className="text-[10px] text-emerald-400 font-semibold">{bp}</div>
                  <div>{name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 4 · PROPERTY WORKSPACE */}
      <Section tid="section-workspace" bg="slate">
        <Kicker>Property Workspace</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Every property becomes a command center where all information, documents, analyses, and activity live together.
        </h2>
        <ProductScreen title="workspace / 42 Coach Test Ln, Cleveland OH">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            {[
              ["ARV",           "$248,000", "BP-004"],
              ["Repair",        "$31,200",  "BP-003"],
              ["Deal Score",    "87",       "BP-005"],
              ["Strategy",      "Subject-To", "BP-014"],
              ["Market Tier",   "Hot",       "BP-024"],
              ["Coach Confidence", "82%",  "BP-025"],
            ].map(([k, v, bp]) => (
              <div key={k} className="rounded-lg border border-slate-200 p-3">
                <div className="text-xs uppercase text-slate-500">{k}</div>
                <div className="text-lg font-bold">{v}</div>
                <div className="text-[10px] text-emerald-600 font-semibold mt-1">{bp}</div>
              </div>
            ))}
          </div>
        </ProductScreen>
      </Section>

      {/* 5 · INTELLIGENCE ENGINES */}
      <Section tid="section-engines" bg="white">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <Kicker>Intelligence Engines</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-2xl">
              Nine deterministic engines. One explainable coach.
            </h2>
          </div>
          <Link to="/products" className="text-emerald-600 font-semibold flex items-center gap-1 hover:underline">
            Explore all products <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            [Brain, "AI Acquisition Coach", "Grounded next-best-action per property. Every claim cites BP + version.", "BP-025"],
            [LayoutDashboard, "Executive Dashboard", "Business health in seconds — 40+ KPIs, six dimensions, elite/strong/healthy/watch bands.", "BP-019/020"],
            [LineChart, "Market Intelligence", "Health score, strategy-fit, forecasts, and alerts per ZIP.", "BP-024"],
            [Users2, "Buyer Intelligence", "Rank verified buyers by fit + funding + track record.", "BP-022"],
            [Building2, "Portfolio Intelligence", "Cash-flow health + cap rate + expiring funding across your holdings.", "BP-023"],
            [Target, "Strategy Comparison", "Every strategy scored side-by-side with numeric Why-Not audit.", "BP-014"],
          ].map(([Icon, title, desc, bp]) => (
            <div key={title} className="rounded-xl border border-slate-200 p-5 hover:border-emerald-500 hover:shadow-lg transition-all">
              <Icon className="w-6 h-6 text-emerald-600" />
              <div className="mt-3 font-bold text-lg">{title}</div>
              <div className="mt-1 text-sm text-slate-600">{desc}</div>
              <div className="mt-3 text-[10px] font-mono text-slate-500">{bp}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 6 · STRATEGIES */}
      <Section tid="section-strategies" bg="navy">
        <Kicker>Every Strategy. Every Deal.</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Whether you wholesale, flip, hold, or get creative — the platform speaks your strategy.
        </h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {["Wholesale", "Fix & Flip", "Buy & Hold", "BRRRR", "Wholetail", "Subject-To", "Seller Finance", "Novation"].map((s) => (
            <div key={s} className="rounded-lg bg-white/5 border border-white/10 px-4 py-6 text-center font-semibold hover:bg-white/10 transition-colors">
              {s}
            </div>
          ))}
        </div>
      </Section>

      {/* 7 · AI COACH */}
      <Section tid="section-coach" bg="white">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>AI Acquisition Coach</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">
              AI doesn't replace your judgment. It <span className="text-emerald-600">explains the evidence.</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Every Coach recommendation cites the source engine, the version, and the exact evidence path. If data is missing, confidence caps at 60% — the Coach never invents a number.
            </p>
            <Link to="/products/ai-acquisition-coach" className="mt-6 inline-flex items-center gap-1 text-emerald-600 font-semibold hover:underline">
              How the Coach works <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <ProductScreen title="ai coach / recommendation">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-xs uppercase text-emerald-800">Recommendation</div>
              <div className="text-2xl font-bold text-emerald-900">Subject-To</div>
              <div className="text-xs text-emerald-700 mt-1">Confidence 82% · 8 evidence sources</div>
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {[["Deal Score", "87", "BP-005"], ["Market Health", "83", "BP-024"], ["Strategy Winner", "Subject-To", "BP-014"]].map(([k, v, bp]) => (
                <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-1">
                  <span className="text-slate-600">{k}</span>
                  <span className="font-semibold">{v}</span>
                  <span className="text-[10px] text-emerald-600 font-semibold">{bp}</span>
                </div>
              ))}
            </div>
          </ProductScreen>
        </div>
      </Section>

      {/* 8 · EXECUTIVE DASHBOARD */}
      <Section tid="section-executive" bg="slate">
        <Kicker>Executive Dashboard</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Know the health of your business in seconds — not spreadsheets.
        </h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Health", "90 / 100", "ELITE"],
            ["Revenue", "$427K", "30d"],
            ["Close Rate", "38%", "+4pp"],
            ["Pipeline", "$1.8M", "27 deals"],
          ].map(([k, v, s]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase text-slate-500">{k}</div>
              <div className="text-2xl font-bold text-slate-900">{v}</div>
              <div className="text-xs text-emerald-600 font-semibold mt-1">{s}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 9 · PLATFORM ADMIN */}
      <Section tid="section-platform" bg="white">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Kicker>Enterprise Governance</Kicker>
            <h2 className="mt-2 text-3xl md:text-4xl font-bold">
              Six-role RBAC. Immutable audit. MFA + IP allowlist. Impersonation with a paper trail.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Every platform action is logged forever. Every mutation optionally gated by TOTP. Every workspace suspendable in one click. Built for teams that will be audited.
            </p>
          </div>
          <div className="rounded-2xl bg-[#0B1F3A] text-white p-6">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-400">Platform Console</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              {["Tenants", "Roles", "Audit Log", "Feature Flags", "Support", "Announcements"].map((f) => (
                <div key={f} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">{f}</div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* 10 · CUSTOMER SUCCESS (placeholder testimonials) */}
      <Section tid="section-testimonials" bg="slate">
        <Kicker>Customer Success</Kicker>
        <h2 className="mt-2 text-3xl md:text-4xl font-bold max-w-3xl">
          Investors moving faster with evidence-backed decisions.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            ["Solo wholesaler", "Cut analysis time from 90 minutes to 6.", "42 deals · 12 mo"],
            ["Acquisition team lead", "The Coach's Why-Not panel ended our strategy debates.", "18 users · Team plan"],
            ["Fund PM", "Executive dashboard replaced three BI dashboards on Monday morning.", "Enterprise · 6 markets"],
          ].map(([role, quote, meta]) => (
            <div key={role} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-emerald-600 text-2xl font-serif leading-none">"</div>
              <p className="mt-2 text-slate-700 italic">{quote}</p>
              <div className="mt-4 text-sm">
                <div className="font-semibold text-slate-900">— {role}</div>
                <div className="text-xs text-slate-500 mt-0.5">{meta}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 11 · PRICING PREVIEW — synced with /pricing + backend billing.py */}
      <Section tid="section-pricing" bg="white">
        <div className="text-center">
          <Kicker>Simple pricing</Kicker>
          <h2 className="mt-2 text-3xl md:text-4xl font-bold">
            Start free. Scale with your business.
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Every new signup gets <span className="font-semibold text-slate-900">14 days of full Pro access</span> automatically. No credit card required · Cancel anytime.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {[
            {
              name: "Starter", price: "$0", period: "/mo",
              tag: "Free forever · Kick the tires",
              dot: "#94a3b8",
              features: [
                "5 active properties",
                "Full Immutable Timeline",
                "2 Pitch Links (watermarked)",
                "Jarvis AI — 3 messages/day",
                "3 Property Reports / mo",
                "10 Power Team contacts",
                "1 User",
              ],
            },
            {
              name: "Basic", price: "$97", period: "/mo",
              tag: "One-market solo · CRM essentials",
              dot: "#4f46e5",
              features: [
                "Up to 50 active properties",
                "Two-way SMS + Twilio dialer (BYO)",
                "10 BatchData skip-trace credits / mo",
                "3 DocuSign envelopes (or BYO unlimited)",
                "5 Pitch Links (no watermark + OG)",
                "Jarvis AI — 10 messages / day",
                "Daily Briefing email (7am)",
                "Email support",
              ],
            },
            {
              name: "Pro", price: "$197", period: "/mo",
              tag: "Full AI operating system",
              primary: true, badge: "⭐ Most Popular",
              dot: "#3b82f6",
              features: [
                "Everything in Basic, plus:",
                "Unlimited active properties",
                "🔥 AI Deal Intelligence Trilogy",
                "🤖 Jarvis Unlimited + British Voice",
                "🎯 AI Lead Scoring 0-100",
                "🔍 AI Rehab Scan — 10/day",
                "150 skip-trace credits / mo",
                "Unlimited Pitch Links",
                "Daily Briefing + Evening Wrap",
                "🏛 Perrin Insider community",
              ],
            },
            {
              name: "Team", price: "$397", period: "/mo",
              tag: "Brokerages · Acquisition teams",
              dot: "#a855f7",
              features: [
                "Everything in Pro, plus:",
                "5 team seats (+$39/seat · +$19/VA)",
                "400 skip-trace credits / mo",
                "30/day AI Rehab Scans",
                "Team Analytics dashboard",
                "Role-based permissions",
                "Custom branded sender domain",
                "API access",
                "Dedicated Slack + 4h SLA",
              ],
            },
          ].map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl border p-5 bg-white transition-all ${
                p.primary
                  ? "border-transparent shadow-xl ring-2 ring-blue-500"
                  : "border-slate-200 hover:shadow-lg"
              }`}
            >
              {p.badge && (
                <div
                  className="absolute -top-3 left-5 rounded-full text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1"
                  style={{ backgroundColor: p.dot }}
                >
                  {p.badge}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: p.dot }}
                  aria-hidden="true"
                />
                <div className="text-lg font-bold">{p.name}</div>
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-slate-500 min-h-[32px]">
                {p.tag}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className="text-4xl font-bold">{p.price}</div>
                <div className="text-sm text-slate-500">{p.period}</div>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: p.dot }}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/auth?mode=signup"
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className={`mt-6 block text-center rounded-lg font-semibold px-4 py-2 transition-colors ${
                  p.primary
                    ? "text-white"
                    : "border border-slate-300 hover:border-slate-900 text-slate-900"
                }`}
                style={p.primary ? { backgroundColor: p.dot } : undefined}
              >
                {p.name === "Starter" ? "Start free" : "Start 14-day Pro trial"}
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/pricing"
            data-testid="pricing-see-all"
            className="inline-flex items-center gap-1 text-emerald-600 font-semibold hover:underline"
          >
            See full plan comparison &rarr;
          </Link>
        </div>
      </Section>

      {/* 12 · FINAL CTA */}
      <section data-testid="section-final-cta" className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.25),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
          <Rocket className="w-10 h-10 text-emerald-400 mx-auto" />
          <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight">
            Speed is the deal.<br />
            <span className="text-emerald-400">Start closing faster today.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-300 max-w-xl mx-auto">
            Analyze your first property in under 60 seconds. Free trial. No credit card required.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="final-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
